<script setup lang="ts">
import { defineProps } from 'vue';
import Dropdown from '@/dashboard/vue/components/common/Dropdown/Dropdown.vue';
import Menu from '@/dashboard/vue/components/common/Menu/Menu.vue';
import MenuItem from '@/dashboard/vue/components/common/Menu/MenuItem.vue';
import FolderIcon from '@/dashboard/vue/components/icons/FolderIcon.vue';
import { getProjectVisibilityType } from '@/dashboard/types/project_visibility';
import ImageIcon from '@/dashboard/vue/components/icons/ImageIcon.vue';
import TeamIcon from '@/dashboard/vue/components/icons/TeamIcon.vue';
import LockIcon from '@/dashboard/vue/components/icons/LockIcon.vue';
import TransferIcon from '@/dashboard/vue/components/icons/TransferIcon.vue';
import LabelIcon from '@/dashboard/vue/components/icons/LabelIcon.vue';
import DeleteIcon from '@/dashboard/vue/components/icons/DeleteIcon.vue';
import ArrowDownSIcon from '@/dashboard/vue/components/icons/ArrowDownSIcon.vue';
import { GroupMoveItems } from '@/dashboard/vue/components/public/PublicMainDraft/usePublicMainDraft';

const props = defineProps<{
  selectAll: () => void;
  uncheck: () => void;
  deletion: () => void;
  moveTo: (id: number) => void;
  count: number;
  groupMoveItems: GroupMoveItems[];
}>();
</script>

<template>
  <div
    class="pointer-events-none fixed left-zero right-zero z-[1001] mt-s flex w-full justify-end px-m max-tablet:bottom-zero max-tablet:justify-center max-tablet:bg-white dark:max-tablet:bg-gray-900"
  >
    <div class="pointer-events-auto flex items-center gap-xs">
      <div
        class="flex h-[48px] items-center gap-xs rounded-6 bg-white px-xs dark:bg-gray-900 max-tablet:h-[74px] max-tablet:bg-transparent max-tablet:px-zero laptop:shadow-s"
      >
        <div>
          <button
            class="tppr-button s max-tablet:sl info-tip s2 dark:primary min-w-[136px] max-tablet:w-[109px] max-tablet:min-w-[109px]"
            @click="() => props.selectAll()"
          >
            <span class="tppr-button__text">Select all</span>
          </button>
        </div>
        <div>
          <button
            class="tppr-button s max-tablet:sl info-tip s2 dark:primary min-w-[136px] max-tablet:w-[109px] max-tablet:min-w-[109px]"
            @click="() => props.uncheck()"
          >
            <span class="tppr-button__text">Deselect</span>
          </button>
        </div>
      </div>
      <div
        class="flex h-[48px] items-center gap-xs rounded-6 bg-white pl-m pr-xs dark:bg-gray-900 max-tablet:h-[74px] max-tablet:bg-transparent max-tablet:px-zero laptop:shadow-s"
      >
        <span class="text-caption-s text-black dark:text-white max-tablet:hidden">
          Selected {{ props.count }} {{ props.count > 1 ? 'items' : 'item' }}:
        </span>

        <div>
          <button
            class="tppr-button s max-tablet:sl !hidden min-w-[112px] !bg-hard-error max-tablet:!flex max-tablet:w-[109px] max-tablet:min-w-[109px]"
            @click="() => props.deletion()"
          >
            <span class="tppr-button__text">Delete</span>
          </button>

          <Dropdown body-class="overflow-visible right-zero mt-[5px]" class="max-tablet:hidden">
            <template #title>
              <button class="tppr-button s max-tablet:sl gap-x-xs !bg-brand-night-blue">
                <span class="tppr-button__text">Take action</span>
                <ArrowDownSIcon class="stroke-white" />
              </button>
            </template>
            <template #body>
              <Menu class="!shadow-none">
                <MenuItem :only-left="true">
                  <div class="flex items-center gap-[10px] whitespace-nowrap">
                    <TransferIcon class="min-w-[16px] stroke-black dark:stroke-white" />
                    <span class="text-caption-s text-black dark:text-white">Move to...</span>
                  </div>

                  <template #menu>
                    <Menu>
                      <template v-for="group in props.groupMoveItems" :key="group.workspace.id">
                        <MenuItem :only-left="true">
                          <div class="flex items-center gap-[10px] whitespace-nowrap">
                            <LabelIcon class="min-w-[16px] stroke-black dark:stroke-white" />
                            <span class="text-caption-s text-black dark:text-white">{{ group.workspace.name }}</span>
                          </div>

                          <template #menu>
                            <Menu>
                              <template v-for="project in group.projects" :key="project.id">
                                <MenuItem :action="() => moveTo(project.id)">
                                  <div class="flex items-center gap-[10px] whitespace-nowrap">
                                    <ImageIcon v-if="project.temporary" class="min-w-[16px] stroke-black dark:stroke-white" />
                                    <LockIcon
                                      v-else-if="getProjectVisibilityType(project) == 'private'"
                                      :stroke-class="'stroke-black dark:stroke-white'"
                                      :fill-class="'fill-black dark:fill-white'"
                                      class="min-w-[16px]"
                                    />
                                    <TeamIcon
                                      v-else-if="getProjectVisibilityType(project) == 'team'"
                                      class="min-w-[16px] stroke-black dark:stroke-white"
                                    />
                                    <FolderIcon
                                      v-else-if="getProjectVisibilityType(project) == 'link'"
                                      class="min-w-[16px] stroke-black dark:stroke-white"
                                    />
                                    <span class="text-caption-s text-black dark:text-white">{{ project.name }}</span>
                                  </div>
                                </MenuItem>
                              </template>
                            </Menu>
                          </template>
                        </MenuItem>
                      </template>
                    </Menu>
                  </template>
                </MenuItem>

                <MenuItem :action="props.deletion">
                  <div class="flex items-center gap-[10px] whitespace-nowrap">
                    <DeleteIcon class="min-w-[16px] stroke-red" />
                    <span class="text-caption-s text-red dark:text-white">Delete selected</span>
                  </div>
                </MenuItem>
              </Menu>
            </template>
          </Dropdown>
        </div>
      </div>
    </div>
  </div>
</template>
